import { buildPauseAck } from "../lib/ssml";

// 読み上げ文言の単一ソース (spec: docs/superpowers/specs/2026-07-26-prerendered-tts-design.md §4.1)。
//
// ここに集約する理由は 2 つある:
//   1. 事前生成 (pnpm tts:build) の対象を 1 箇所で数え上げられる
//   2. コンポーネント側に定数を残すと「生成対象リスト」との二重管理になり、
//      ズレるとその文言だけ API 合成のままコストが残る
//
// 動的に組み立てる文言 (氏名復唱 / 聞き取り結果の読み返し) はここに入れない。
// アンケートの質問文は public/surveys/*.json が単一ソースなので入れない
// (ビルドスクリプトが JSON を走査する)。
//
// pause 付き SSML (buildPauseAck) を使う文言は、断片 (msg/pauseMs/tail) ではなく
// 組み立て済みの完成形をここに持つ。実際に TTS API へ送られるのはこの完成形の
// 文字列であり、allPhrases() が事前生成対象を数え上げる起点になるため、断片の
// ままでは生成対象として機能しない (buildPauseAck 自体は決定的な文字列連結のみで
// 条件分岐や実行時計算を持たないため、ここで呼び出しても「ロジックを持たない」
// という制約には反しない)。
export const TTS_PHRASES = {
  // --- スピーカー確認 ---
  // D25: マイク未検証の段階なので、声ではなくボタンでの応答を促す。読み上げ時は
  // toSpeechText が鉤括弧を落とすため「画面の 聞こえました ボタンを押してください」と
  // 読まれる。「ボタン」を後置しているのは、鉤括弧が消えても文が破綻しない語順にするため。
  speakerTest: "テスト音声です。聞こえましたら、画面の「聞こえました」ボタンを押してください。",
  askAgain: "「はい」か「いいえ」でお答えください。",
  useButton: "画面のボタンでお答えください。",

  // --- 氏名確認 ---
  namePrompt: "ありがとうございます。次にマイクを確認します。お名前をフルネームでお話しください。",
  nameRetry: "もう一度お名前をお話しください。",
  nameFailed: "うまく聞き取れませんでした。もう一度お話しください。",
  nameGiveUp: "うまくいかないようです。画面の「もう一度試す」を押してください。",
  // 収束しないと判定したときの退避案内 (spec 2026-07-26 D25-3 / D25-7)。nameGiveUp と
  // 別に持つのは、あちらが「もう一度試す」を押させる文言だから — いま収束しないと
  // 判定したばかりの音声ループへ戻すことになる。こちらはキーボードを指す。
  nameEscalate: "うまく聞き取れないようです。画面のキーボードでもご入力いただけます。",

  // 氏名判定 (spec 2026-07-25 §6) の verdict 別再入力案内。読み上げと画面表示に同じ
  // 文字列を使うため、他の文言と同じくここを単一ソースにする (NameCapture 側に直書きすると
  // 事前生成の数え上げから漏れ、この 3 件だけ API 合成が残る)。
  // 「姓」「名字」ではなく「苗字」なのは TTS 対策 (2026-08-04)。「姓」は「しょう」と
  // 読まれ (百姓の読み)、読み替えで「せい」に差し替える案も「せいと」が「生徒」に
  // 融合して不採用。「名字」も実機聴取で読みが崩れた。同じ voice / model で 4 案を
  // 合成して聴き比べ、「苗字」を採った (経緯は spec 2026-07-26 §9.2.1)。
  nameRejectUnrelated: "お名前が聞き取れませんでした。苗字と名前を、ゆっくりお話しください。",
  nameRejectPartial: "下のお名前も一緒に、フルネームでお願いします。",
  nameRejectRefusal: "お名前は回答の集計にのみ使います。お願いできますでしょうか。",

  // --- 遊び方チュートリアル ---
  tutorialBeat1:
    "わたしは音声アシスタントです。今週の振り返りについて、5 つほど順番におうかがいします。",
  tutorialBeat2: "質問の読み上げが終わると、マイクが開きます。そのまま、お話しください。",
  tutorialBeat3: "お話しが終わったら、少しだけ待ってくださいね。わたしが内容を確認します。",

  // --- 事前チェック完了 ---
  ready: "準備が整いました。それでは、アンケートを始めましょう。",

  // --- 本編 ---
  greeting:
    "こんにちは。本日はお時間をいただき、ありがとうございます。今週の振り返りについて、5 つほどおうかがいします。マイクに向かって、いつもの言葉でお気軽にお話しください。",
  // 「前回の」を付けているのは TTS 対策 (2026-07-26)。「続きから〜」と文頭に置くと
  // ElevenLabs が「続き」の語頭を弱く発音したりアクセントを崩したりして安定しなかった
  // (同じテキストで 3 回引き直しても改善せず)。文頭に助走を置くと安定して発音される。
  // 文脈としても「前回の続き」の方が意味が明確なので、画面表示ごとこの文言にしている。
  resumePrompt: "前回の続きから始めましょうか。それとも、最初からになさいますか。",
  answerThanks: "✓ ありがとうございます。",

  // --- 無音 escalation (3 段) ---
  silence0: "聞こえていますでしょうか。お話しをお待ちしていますね。",
  silence1:
    "もしかすると、マイクが届いていないかもしれません。よろしければ、文字での入力に切り替えていただけます。",
  silence2: "少し休憩なさいますか。準備ができましたら、🎙 をタップして再開してください。",

  // --- エラー ---
  errorShortUtterance: "すみません、うまく聞き取れませんでした。もう一度お聞かせください。",
  // 「お手数ですが」を外しているのは TTS 対策 (2026-07-26)。実機の聴取確認で
  // 「お手数」が正しく読まれなかった (「おてすう」にならない)。ひらがな表記は
  // 画面に「おてすうですが」と出て業務アンケートの案内として幼く見えるため、
  // 語ごと落とす形にした。「〜ようです」「〜いただけますか」で丁寧さは保てている。
  errorSttFailed: "うまく聞き取れなかったようです。もう一度お話しいただけますか。",
  errorMicLost:
    // 「お手数ですが」の除去に加え、47 → 40 字に短縮し 🎙 を言葉にした (2026-07-26)。
    // 元の文は滑舌が悪く聞き取りづらかった。長文の早口化が主因とみられる。
    // 絵文字を「マイクのボタン」に開いたのは、読み上げの安定に加えて画面表示としても
    // 絵文字を認識できない人に伝わるため (UI としても改善)。
    "マイクとの接続が切れたようです。もう一度、マイクのボタンをタップして許可してください。",

  // --- 送信前の確認 (pause 付き SSML。showSummary() が使う) ---
  // 後半はフォームなので、声で答える案内をしてはいけない (spec 2026-08-04 U3)。
  // ここでマイクは止まっており、「はい」と言っても何も起きない。
  // **文言を変えたので事前生成音声のキーが変わる** — `pnpm tts:build` が要る
  // (未生成でも API 合成に落ちるだけで壊れない)。
  submitConfirm: buildPauseAck(
    "ありがとうございました。最後に、画面でご回答をご確認ください",
    2000,
    "直したいところがあれば、文字で直していただけます。"
  ),
  // 聞き取り失敗時の共通再依頼 (pause 付き SSML。resume-prompt / summary / 通常回答の
  // 3 経路の catch 節で共通利用)
  retryGeneric: buildPauseAck(
    "すみません、うまく聞き取れませんでした",
    500,
    "もう一度お聞かせいただけますか。"
  ),
  // 回答として成立していないと判定したときの聞き直し (spec 2026-07-28 §3.4)。
  // 「聞き取れませんでした」と言わないのが要点 — 聞き取れてはいるので、そう言うと
  // 画面と声が嘘をつき、ユーザーはマイクや発音を疑い始める。
  answerTooThin: "すみません、もう少し詳しくお聞かせいただけますか。",
  // 2 回目以降。この直後に設問の吹き出しを積み直すので、ここでは設問文を連結しない
  // (連結すると collectPhrases が拾えず、この文言だけ API 合成が残る)。
  answerReask: "もう一度お聞きします。",
  // 続きから/最初から の意図が不明瞭だった時の選択肢案内 (pause 付き inline SSML)
  resumeChoice: '「続きから」か「最初から」<break time="500ms" />とお話しください。',
  // 送信/修正 の意図が不明瞭だった時の選択肢案内 (pause 付き inline SSML)
  submitChoice: '「送信」か「修正」<break time="500ms" />とお話しください。',

  // --- 送信完了 (spec 2026-07-26 D4) ---
  // 「ありがとうございました」で始めない: 直前の submitConfirm が同じ言葉で始まるため、
  // 続けて 2 回聞くことになる。画面の見出しは「ご回答ありがとうございました」のままでよい
  // (目で読む言葉と耳で聞く言葉は連続しないため重複しない)。
  surveyCompleted:
    "お疲れさまでした。アンケートはこれで終了です。画面を閉じていただいて大丈夫です。",
} as const;

// TTS_PHRASES の全値を返す。事前生成スクリプト (pnpm tts:build) がここから
// 「音声化すべき文言の全件リスト」を数え上げる起点になる。
export function allPhrases(): string[] {
  return Object.values(TTS_PHRASES);
}
